import { NgIf } from '@angular/common';
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FileDragDropDirective } from '@directives/file-drag-drop.directive';
import { ValidatorFunctions } from '@enums/validator-functions';
import { Question } from '@models/dynamic-forms/question';

@Component({
  selector: 'app-file-input',
  templateUrl: './file-input.component.html',
  styleUrl: './file-input.component.scss',
  imports: [FileDragDropDirective, NgIf, ReactiveFormsModule],
})
export class FileInputComponent {
  @Input() public form: FormGroup;
  @Input() public question: Question;
  @Input() public disabled: boolean = false;
  @ViewChild('fileInput', { static: false }) public fileInput: ElementRef<HTMLInputElement>;

  /**
   * Gets the form control associated with the current question.
   */
  public get control(): AbstractControl {
    return this.form.controls[this.question.id];
  }

  /**
   * Gets the accepted file types for the file input.
   */
  public get acceptedFileTypes(): string {
    const fileTypeValidator = this.question.validators.find(
      (validator) => validator.validatorFn.name === ValidatorFunctions.FileType,
    );

    return fileTypeValidator ? fileTypeValidator.validatorFn.params[0].join(',') : '';
  }

  /**
   * Updates the form control with file details when a file is selected.
   *
   * @param {FileList} files the list of files selected by the user
   */
  public handleFileChange(files: FileList): void {
    if (files.length !== 1) {
      return;
    }

    this.control.patchValue(files[0]);
    this.control.markAsTouched();
  }

  /**
   * Removes the selected file from the form control.
   *
   * @param {Event} event the event triggered when the remove button is clicked
   */
  public removeFile(event: Event): void {
    event.stopPropagation();
    this.control.patchValue('');
  }

  /**
   * Triggers the file input dialog to open, allowing the user to select a file.
   */
  public triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }
}
